// The Format menu's rule (#344): wrap or unwrap the selected text in a message box. A pure function on the text and the
// selection, so the web bar, the iOS and Mac menus and the Android selection menu all do the same thing. The Swift and
// Kotlin ports run shared/cases/format-selection.json against their copy.
//
// Offsets are UTF-16 code units (a JS string index, an NSRange, a Kotlin String index). Every mark is ASCII, so the
// rule never splits a surrogate pair, and it never changes a character's width: it only inserts or deletes marks.
//
// Inline items (bold, italic, underline, strike, code, spoiler) put their marks round the selection: ** * __ ~~ ` ||.
//   - Inside a code span or a code block every inline item does nothing: the marks would be text. Selecting a span's
//     whole text, or text that holds the span, puts the marks round the span.
//   - Spaces, tabs and line breaks at the edges of the selection stay outside the marks ("** x **" is not bold in
//     Discord, and "* x *" is not italic). A selection of only those is an empty selection after it.
//   - An empty selection gets both marks, with the caret between them. With the caret in an open span and just before
//     its closing marks, it steps over them instead ("**hi|**" to "**hi**|"), so item, type, item, type makes one span.
//     With the caret right after the closing marks of a span of this item, the key does nothing ("**hi**|", and
//     "***hi***|" for bold and for italic), so the two closing keys of stacked items work in either order.
//   - The item is ON when its marks are a pair round the words, just inside the selection, just outside it, or split
//     across its edge. Marks of other items may sit between (**~~word~~** has bold on). Pressing an ON item takes the
//     marks off, from the outer end of the run; the words stay selected, so another item stacks.
//   - The parser decides whether two runs are one span (`parseSource`, shared/src/markdown.ts), not a count of marks:
//     in "**a** and **b**" the runs round a..b belong to two spans; in "2*3 and *x*" the first * belongs to none; in
//     "`**x**`" the ** are text. Where the parser pairs a mark with a later one, the words after it are plain.
//   - "*" and "**" share a character: a pair of 1 is italic, 2 is bold, 3 is both. A run counts the same on both sides
//     of the selection edge, so italic on **bold** never takes half of the bold marks.
//   - Code on words that hold backticks uses the shortest fence whose length is not the length of a run in them, and puts
//     a space inside the fence each side when the words start or end with a backtick. A fence of three is a code block to
//     the parser (shared/src/markdown.ts), so words with both a single and a double run make a block.
//   - The parser drops that pair of spaces ("`` `a ``" is the text "`a"). The text of a padded span is its content without
//     them: Code on it takes the fence off, and another item puts its marks round the span.
// Quote puts "> " at the start of each selected line; it is ON when every selected line has one. A selection that ends
// right after a line break does not include the next line.
// Code block puts "```\n" before the selection and "\n```" after it; it is ON when those fences (the first with an
// optional language, "```js\n") are just outside the selection or at its edges.
// The rule puts the item's marks in. It does not repair what is already in the text, so it can not promise the parser
// reads them as that item: marks that cross another span's marks stay crossed, and a mark earlier in the text that the
// parser pairs with a later one keeps the words after it plain ("a**b and **x**": Bold on x wraps it, and x is still not
// bold). On words with no such mark in the text, it makes what shared/src/markdown.ts parses as that item.

import { parseSource, type SrcNode } from "./markdown.ts";

export type FormatItem = "bold" | "italic" | "underline" | "strike" | "code" | "spoiler" | "quote" | "codeblock";

/** The items in menu order: the order of the buttons, and of `activeFormats`. */
export const FORMAT_ITEMS: readonly FormatItem[] = ["bold", "italic", "underline", "strike", "code", "spoiler", "quote", "codeblock"];

export interface FormatResult {
  text: string;
  /** The selection after the change, in UTF-16 units. */
  start: number;
  end: number;
}

const MARKS: Record<string, string> = { bold: "**", italic: "*", underline: "__", strike: "~~", code: "`", spoiler: "||" };
const MARK_CHARS = "*_~|`";
const FENCE = "```";
const FENCE_CLOSE = "\n```";
const LANG_CHARS = /[A-Za-z0-9_+#.-]/;

const isSpace = (c: string) => c === " " || c === "\t" || c === "\n" || c === "\r";
const isMark = (c: string | undefined) => c !== undefined && MARK_CHARS.includes(c);

/** The selection in range and in order. */
function clamp(text: string, start: number, end: number): [number, number] {
  const a = Math.min(Math.max(start, 0), text.length);
  const b = Math.min(Math.max(end, 0), text.length);
  return a <= b ? [a, b] : [b, a];
}

/** Takes spaces off the edges of a selection; a selection of only spaces becomes an empty one at its end. */
function trimmed(text: string, start: number, end: number): [number, number] {
  if (start === end) return [start, end];
  let a = start;
  let b = end;
  while (a < b && isSpace(text[a]!)) a++;
  while (b > a && isSpace(text[b - 1]!)) b--;
  return a === b ? [end, end] : [a, b];
}

// ---- Inline marks ----

interface Run {
  c: string;
  from: number;
  to: number;
}

interface Halo {
  /** The runs of marks outward from the words, a side each. */
  lefts: Run[];
  rights: Run[];
}

/**
 * Is a pair of runs, `l` and `r` marks long, this item's marks? "*" counts a pair of 1 (italic) and of 2 (bold) apart.
 * Backticks: 1 or 2 make a code span; 3 or more are a code block's fence, not code.
 */
function fits(item: FormatItem, l: number, r: number): boolean {
  const k = Math.min(l, r);
  if (item === "italic") return k % 2 === 1;
  if (item === "code") return k >= 1 && Math.max(l, r) <= 2;
  return k >= 2;
}

/**
 * The marks round the (trimmed) selection, outward from its words, a run of one character at a time ("**~~word~~**"
 * is two runs a side). The words are the selection without the marks it holds at its edges; a selection of only marks
 * is the words itself.
 */
function haloOf(text: string, start: number, end: number): Halo {
  let a = start;
  let b = end;
  while (a < b && isMark(text[a])) a++;
  if (a === b) {
    a = start;
  } else {
    while (b > a && isMark(text[b - 1])) b--;
  }
  const lefts: Run[] = [];
  for (let i = a; i > 0 && isMark(text[i - 1]); ) {
    const c = text[i - 1]!;
    let j = i;
    while (j > 0 && text[j - 1] === c) j--;
    lefts.push({ c, from: j, to: i });
    i = j;
  }
  const rights: Run[] = [];
  for (let i = b; i < text.length && isMark(text[i]); ) {
    const c = text[i]!;
    let j = i;
    while (j < text.length && text[j] === c) j++;
    rights.push({ c, from: i, to: j });
    i = j;
  }
  return { lefts, rights };
}

/** The pairs of runs that agree on a character, outward from the words; the search stops at the first that differ. */
function pairs(h: Halo): [Run, Run][] {
  const out: [Run, Run][] = [];
  for (let k = 0; k < Math.min(h.lefts.length, h.rights.length); k++) {
    if (h.lefts[k]!.c !== h.rights[k]!.c) break;
    out.push([h.lefts[k]!, h.rights[k]!]);
  }
  return out;
}

/** A span the parser (`parseSource` in shared/src/markdown.ts) finds in the text: its type, where it is, its marks. */
interface Span {
  t: string;
  from: number;
  to: number;
  /** The length of the mark that opens it, and of the one that closes it. */
  open: number;
  close: number;
  /** A code span's padding: 1 when the parser drops one space each side of the content (`` `a ``), else 0. */
  pad: number;
}

const NODE: Record<string, string> = { bold: "strong", italic: "em", underline: "u", strike: "s", code: "code", spoiler: "spoiler" };

const srcLength = (n: SrcNode): number => ("v" in n ? n.v.length : n.c.reduce((sum, k) => sum + srcLength(k), 0));

/** Does the parser drop one space each side of this code content? The same test as `codeText` in markdown.ts. */
const isPadded = (content: string) => content.length > 2 && content.startsWith(" ") && content.endsWith(" ") && content.trim() !== "";

/** Every span in the text, outer before inner. The leaves of the parse give back the text, so their lengths are offsets. */
function spansOf(text: string): Span[] {
  const out: Span[] = [];
  const walk = (nodes: SrcNode[], from: number) => {
    let at = from;
    for (const n of nodes) {
      const length = srcLength(n);
      if ("c" in n) {
        const first = n.c[0];
        const last = n.c[n.c.length - 1];
        const open = first?.t === "mark" ? first.v.length : 0;
        const close = n.c.length > 1 && last?.t === "mark" ? last.v.length : 0;
        const pad = n.t === "code" && close > 0 && isPadded(text.slice(at + open, at + length - close)) ? 1 : 0;
        out.push({ t: n.t, from: at, to: at + length, open, close, pad });
        walk(n.c, at);
      }
      at += length;
    }
  };
  walk(parseSource(text), 0);
  return out;
}

/**
 * Is the selection inside code? A span's whole text, or text that holds the span, is not: the marks go round the span.
 * A span's text is its content without the parser's padding: in "`` `a ``" the text is "`a".
 */
function insideCode(spans: Span[], a: number, b: number): boolean {
  return spans.some((k) => {
    if (k.t !== "code" && k.t !== "codeblock") return false;
    const cs = k.from + k.open;
    const ce = k.to - k.close;
    return cs <= a && b <= ce && (k.t === "codeblock" || a !== cs + k.pad || b !== ce - k.pad);
  });
}

/** The code span with padding whose text, without the padding, is exactly the selection. */
function paddedCode(spans: Span[], a: number, b: number): Span | undefined {
  return spans.find((k) => k.t === "code" && k.pad > 0 && a === k.from + k.open + k.pad && b === k.to - k.close - k.pad);
}

/** The marks that make `item` ON: the pair of runs, and how many marks to cut from each. */
function findMarks(text: string, start: number, end: number, item: FormatItem): { left: Run; right: Run; n: number } | null {
  const mark = MARKS[item]!;
  let spans: Span[] | null = null;
  for (const [l, r] of pairs(haloOf(text, start, end))) {
    if (l.c !== mark[0] || !fits(item, l.to - l.from, r.to - r.from)) continue;
    // The parser decides whether the two runs are one span: marks earlier in the text, a pair on each side of the words
    // ("**a** and **b**") and code ("`**x**`") all change what the runs are. Nothing between them is an empty pair.
    if (l.to !== r.from) {
      spans ??= spansOf(text);
      const one = spans.some((k) => k.t === NODE[item] && k.from >= l.from && k.from + k.open <= l.to && k.to <= r.to && k.to - k.close >= r.from);
      if (!one) continue;
    }
    // A code span's whole run goes; the others cut their own length from the outer end of the run.
    return { left: l, right: r, n: item === "code" ? Math.min(l.to - l.from, r.to - r.from) : mark.length };
  }
  return null;
}

/** Where a position goes when the characters in `cuts` (sorted, apart) are deleted. */
function afterCuts(pos: number, cuts: [number, number][]): number {
  let p = pos;
  for (const [from, to] of cuts) p -= Math.max(0, Math.min(pos, to) - from);
  return p;
}

/**
 * With the caret in the words of a span and just before its closing marks, the number of characters to step over, else
 * 0. "**hi|**": a second press of the key leaves the span and types no new pair. The marks of spans inside this one
 * that close at the same place go with it ("***hi|***" in a bold press), so the caret ends outside both.
 */
function closingAt(spans: Span[], pos: number, item: FormatItem): number {
  for (const k of spans) {
    if (k.t !== NODE[item] || k.close === 0 || k.from + k.open >= pos) continue;
    // Walk back from the span's closing mark over the closing marks of the spans inside it that end where it starts.
    let cur = k.to - k.close;
    for (;;) {
      if (cur === pos) return k.to - pos;
      const inner = spans.find((m) => m !== k && m.to === cur && m.close > 0 && m.from >= k.from + k.open && cur - m.close >= pos && m.from + m.open < cur - m.close);
      if (!inner) break;
      cur -= inner.close;
    }
  }
  return 0;
}

/**
 * Is the caret right after the closing marks of a span of this item? The marks of spans round it that close at the same
 * place count: after "***hi***" (bold, then italic) both items have just closed a span. The key does nothing there,
 * so bold, italic, and the two closing keys in either order make "***hi***", not "***hi****".
 */
function justClosed(spans: Span[], pos: number, item: FormatItem): boolean {
  const walk = (at: number, within: number): boolean =>
    spans.some((k) => k.to === at && k.close > 0 && k.from >= within && k.from + k.open < at - k.close && (k.t === NODE[item] || walk(at - k.close, k.from + k.open)));
  return walk(pos, 0);
}

function inline(text: string, start: number, end: number, item: FormatItem): FormatResult {
  const [a, b] = trimmed(text, start, end);
  const spans = spansOf(text);
  if (a === b) {
    const skip = closingAt(spans, a, item);
    if (skip) return { text, start: a + skip, end: a + skip };
    if (justClosed(spans, a, item)) return { text, start, end };
  }
  // Inside code the marks are text: an item there does nothing, and marks that look like the item's are not its own.
  if (insideCode(spans, a, b)) return { text, start, end };
  const padded = paddedCode(spans, a, b);
  if (padded && item === "code") {
    // The selection is the text of a padded code span: take the fence and the padding off.
    const cut = padded.open + padded.pad;
    const cuts: [number, number][] = [
      [padded.from, padded.from + cut],
      [padded.to - cut, padded.to],
    ];
    const out = text.slice(0, cuts[0]![0]) + text.slice(cuts[0]![1], cuts[1]![0]) + text.slice(cuts[1]![1]);
    return { text: out, start: afterCuts(a, cuts), end: afterCuts(b, cuts) };
  }
  // The other items see a padded code span as the whole span, fence and all: the padding space hides the fence from
  // the search for marks round the words.
  const [sa, sb] = padded ? [padded.from, padded.to] : [a, b];
  const found = findMarks(text, sa, sb, item);
  if (found) {
    const { left, right, n } = found;
    const cuts: [number, number][] = [
      [left.from, left.from + n],
      [right.to - n, right.to],
    ];
    const out = text.slice(0, cuts[0]![0]) + text.slice(cuts[0]![1], cuts[1]![0]) + text.slice(cuts[1]![1]);
    return { text: out, start: afterCuts(a, cuts), end: afterCuts(b, cuts) };
  }
  if (item === "code") {
    // The shortest fence that is not the length of a run in the words. Words that start or end with a backtick get a
    // space inside the fence each side; the parser drops that pair of spaces.
    const words = text.slice(a, b);
    const runs = new Set((words.match(/`+/g) ?? []).map((run) => run.length));
    let n = 1;
    while (runs.has(n)) n++;
    const fence = "`".repeat(n);
    const pad = words.startsWith("`") || words.endsWith("`") ? " " : "";
    const open = fence + pad;
    return { text: text.slice(0, a) + open + words + pad + fence + text.slice(b), start: a + open.length, end: b + open.length };
  }
  const mark = MARKS[item]!;
  // Marks inside a code span are just text, so they go round it. The words stay selected.
  let from = a;
  let to = b;
  const ticks = pairs(haloOf(text, sa, sb)).filter(([l]) => l.c === "`").pop();
  if (ticks) {
    from = ticks[0].from;
    to = ticks[1].to;
  }
  return { text: text.slice(0, from) + mark + text.slice(from, to) + mark + text.slice(to), start: a + mark.length, end: b + mark.length };
}

// ---- Quote ----

const QUOTE = "> ";

interface Lines {
  /** The index of the first character of the first selected line. */
  from: number;
  /** The selected lines, without their line breaks. */
  lines: string[];
}

/** The lines the selection touches. A selection that ends right after a line break leaves the next line out. */
function selectedLines(text: string, start: number, end: number): Lines {
  const last = end > start && text[end - 1] === "\n" ? end - 1 : end;
  let from = start;
  while (from > 0 && text[from - 1] !== "\n") from--;
  let to = last;
  while (to < text.length && text[to] !== "\n") to++;
  return { from, lines: text.slice(from, to).split("\n") };
}

function quote(text: string, start: number, end: number): FormatResult {
  const { from, lines } = selectedLines(text, start, end);
  const off = lines.every((l) => l.startsWith(QUOTE));
  // Where each line starts in the old text, and how much it grows or shrinks.
  const starts: number[] = [];
  let at = from;
  for (const l of lines) {
    starts.push(at);
    at += l.length + 1;
  }
  const delta = lines.map((l) => (off ? -QUOTE.length : l.startsWith(QUOTE) ? 0 : QUOTE.length));
  const rebuilt = lines.map((l, i) => (off ? l.slice(QUOTE.length) : delta[i] ? QUOTE + l : l)).join("\n");
  const blockEnd = from + lines.join("\n").length;
  const move = (pos: number) => {
    if (pos < from) return pos;
    let shift = 0;
    for (let i = 0; i < lines.length; i++) {
      const lineStart = starts[i]!;
      if (pos < lineStart) break;
      // Inside the line: a position inside the mark we take off lands on the line's start.
      shift += off ? -Math.min(QUOTE.length, pos - lineStart) : delta[i]!;
    }
    return pos + shift;
  };
  return { text: text.slice(0, from) + rebuilt + text.slice(blockEnd), start: move(start), end: move(end) };
}

// ---- Code block ----

/** The length of an opening fence ("```" and an optional language, then a line break) that ends at `pos`; 0 if none. */
function fenceEndingAt(text: string, pos: number): number {
  if (pos < FENCE.length + 1 || text[pos - 1] !== "\n") return 0;
  let i = pos - 1;
  while (i > 0 && pos - 1 - i < 32 && LANG_CHARS.test(text[i - 1]!)) i--;
  return i >= FENCE.length && text.slice(i - FENCE.length, i) === FENCE ? pos - (i - FENCE.length) : 0;
}

/** The length of an opening fence that starts at `pos`; 0 if none. */
function fenceStartingAt(text: string, pos: number): number {
  if (text.slice(pos, pos + FENCE.length) !== FENCE) return 0;
  let i = pos + FENCE.length;
  while (i < text.length && i - pos - FENCE.length < 32 && LANG_CHARS.test(text[i]!)) i++;
  return text[i] === "\n" ? i + 1 - pos : 0;
}

type Fences = { kind: "outside" | "inside"; open: number } | null;

function findFences(text: string, start: number, end: number): Fences {
  const outside = fenceEndingAt(text, start);
  if (outside && text.startsWith(FENCE_CLOSE, end)) return { kind: "outside", open: outside };
  const inside = fenceStartingAt(text, start);
  if (inside && end - start >= inside + FENCE_CLOSE.length && text.endsWith(FENCE_CLOSE, end)) return { kind: "inside", open: inside };
  return null;
}

function codeblock(text: string, start: number, end: number): FormatResult {
  const f = findFences(text, start, end);
  if (!f) {
    const open = `${FENCE}\n`;
    return { text: text.slice(0, start) + open + text.slice(start, end) + FENCE_CLOSE + text.slice(end), start: start + open.length, end: end + open.length };
  }
  if (f.kind === "outside") {
    return { text: text.slice(0, start - f.open) + text.slice(start, end) + text.slice(end + FENCE_CLOSE.length), start: start - f.open, end: end - f.open };
  }
  return { text: text.slice(0, start) + text.slice(start + f.open, end - FENCE_CLOSE.length) + text.slice(end), start, end: end - f.open - FENCE_CLOSE.length };
}

// ---- The two entry points ----

/** Applies one Format item to the selection: puts its marks on, or takes them off when the selection already has them. */
export function formatSelection(text: string, start: number, end: number, item: FormatItem): FormatResult {
  const [a, b] = clamp(text, start, end);
  if (item === "quote") return quote(text, a, b);
  if (item === "codeblock") return codeblock(text, a, b);
  return inline(text, a, b, item);
}

/** The items that are on for the selection, in menu order. The web bar lights those buttons. */
export function activeFormats(text: string, start: number, end: number): FormatItem[] {
  const [a, b] = clamp(text, start, end);
  const [ta, tb] = trimmed(text, a, b);
  const spans = spansOf(text);
  return FORMAT_ITEMS.filter((item) => {
    if (item === "quote") return selectedLines(text, a, b).lines.every((l) => l.startsWith(QUOTE));
    if (item === "codeblock") return findFences(text, a, b) !== null;
    if (insideCode(spans, ta, tb)) return false;
    const padded = paddedCode(spans, ta, tb);
    if (padded && item === "code") return true;
    return padded ? findMarks(text, padded.from, padded.to, item) !== null : findMarks(text, ta, tb, item) !== null;
  });
}
